/**
 * LeavesPage - EL (Earned Leave) Only System
 * - Only EL type (no CL/PL/SL)
 * - Half-day leave support (First Half / Second Half)
 * - 12 EL per year, quarterly credited
 * - Only confirmed employees eligible
 */
import { useEffect, useState, useMemo } from 'react';
import { leaveAPI, exportAPI } from '../lib/api';
import ExportButton from '../components/ExportButton';
import { useAuth } from '../context/AuthContext';
import { useApi, useMutation } from '../hooks/useApi';
import { useTable } from '../hooks/useTable';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../components/ui/popover';
import {
  Alert, AlertDescription, AlertTitle,
} from '../components/ui/alert';
import { toast } from 'sonner';
import { Plus, Loader2, CalendarIcon, Clock, CheckCircle, XCircle, Check, X, Users, FileText, ChevronRight, Info, AlertTriangle, CalendarDays } from 'lucide-react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';

// Shared components
import { DataTable } from '../components/shared/DataTable';
import { SearchInput } from '../components/shared/SearchInput';
import { PageSkeleton } from '../components/shared/LoadingSkeleton';

// Status configuration
const LEAVE_STATUS = {
  pending: { label: 'Pending Manager', variant: 'secondary', icon: Clock },
  manager_approved: { label: 'Pending HR', variant: 'outline', icon: Clock },
  approved: { label: 'Approved', variant: 'default', icon: CheckCircle },
  rejected: { label: 'Rejected', variant: 'destructive', icon: XCircle },
};

// Half-day session options
const HALF_DAY_SESSIONS = [
  { value: 'first_half', label: 'First Half (Morning)' },
  { value: 'second_half', label: 'Second Half (Afternoon)' },
];

export default function LeavesPage() {
  const { user, canApproveLeave, isEmployee, isAdmin, isSuperAdmin, isHR } = useAuth();
  const [activeTab, setActiveTab] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rejectDialog, setRejectDialog] = useState({ open: false, leave: null, level: '' });
  const [rejectReason, setRejectReason] = useState('');
  const [formData, setFormData] = useState({
    leave_type: 'EL',
    start_date: new Date(),
    end_date: new Date(),
    reason: '',
    is_half_day: false,
    half_day_session: null,
  });

  // Data fetching with useApi hook
  const { data: leaves, loading, refresh } = useApi(leaveAPI.getAll);
  const { data: teamLeaves, execute: fetchTeamLeaves } = useApi(leaveAPI.getMyTeam, { immediate: false });
  const { data: balance, loading: balanceLoading, error: balanceError, refresh: refreshBalance } = useApi(
    () => leaveAPI.getMyBalance(new Date().getFullYear()),
    { showErrorToast: false }
  );

  // Create leave mutation
  const { mutate: createLeave, loading: creating } = useMutation(leaveAPI.create, {
    successMessage: 'Leave request submitted',
    onSuccess: () => {
      setDialogOpen(false);
      refresh();
      refreshBalance();
      resetForm();
    }
  });

  // Fetch team leaves for managers
  useEffect(() => {
    if (!isEmployee) {
      fetchTeamLeaves();
    }
  }, [isEmployee]);

  // Table hook for filtering/sorting
  const { items: filteredLeaves, search, setSearch, sort, setSort } = useTable(leaves || [], {
    searchFields: ['employee_name', 'leave_type', 'reason'],
    defaultSort: { field: 'created_at', order: 'desc' }
  });

  // Computed stats
  const stats = useMemo(() => ({
    pending: (leaves || []).filter(l => l.status === 'pending').length,
    managerApproved: (leaves || []).filter(l => l.status === 'manager_approved').length,
    approved: (leaves || []).filter(l => l.status === 'approved').length,
    rejected: (leaves || []).filter(l => l.status === 'rejected').length,
  }), [leaves]);

  // Team pending leaves
  const pendingTeamLeaves = useMemo(() => 
    (teamLeaves || []).filter(l => l.status === 'pending'),
    [teamLeaves]
  );

  // Leaves pending final approval
  const pendingFinalLeaves = useMemo(() => 
    (leaves || []).filter(l => l.status === 'manager_approved'),
    [leaves]
  );

  const resetForm = () => {
    setFormData({
      leave_type: 'EL',
      start_date: new Date(),
      end_date: new Date(),
      reason: '',
      is_half_day: false,
      half_day_session: null,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.is_half_day && !formData.half_day_session) {
      toast.error('Please select First Half or Second Half for half-day leave');
      return;
    }
    
    // For half-day, ensure same date
    const submitData = {
      leave_type: 'EL',
      start_date: formData.start_date.toISOString(),
      end_date: formData.is_half_day ? formData.start_date.toISOString() : formData.end_date.toISOString(),
      reason: formData.reason,
      is_half_day: formData.is_half_day,
      half_day_session: formData.is_half_day ? formData.half_day_session : null,
    };
    
    await createLeave(submitData);
  };

  const handleApproval = async (leave, action, level) => {
    if (action === 'reject') {
      setRejectDialog({ open: true, leave, level });
      return;
    }

    try {
      if (level === 'manager') {
        await leaveAPI.managerApprove(leave.id, 'approve');
      } else {
        await leaveAPI.finalApprove(leave.id, 'approve');
      }
      toast.success(`Leave ${action}d successfully`);
      refresh();
      if (!isEmployee) fetchTeamLeaves();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action}`);
    }
  };

  const handleReject = async () => {
    const { leave, level } = rejectDialog;
    if (!leave) return;

    try {
      if (level === 'manager') {
        await leaveAPI.managerApprove(leave.id, 'reject', rejectReason);
      } else {
        await leaveAPI.finalApprove(leave.id, 'reject', rejectReason);
      }
      toast.success('Leave rejected');
      setRejectDialog({ open: false, leave: null, level: '' });
      setRejectReason('');
      refresh();
      if (!isEmployee) fetchTeamLeaves();
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  // Format days display
  const formatDays = (leave) => {
    if (leave.is_half_day) {
      const session = leave.half_day_session === 'first_half' ? 'First Half' : 'Second Half';
      return `0.5 day (${session})`;
    }
    const days = leave.days_count || 1;
    return `${days} day${days > 1 ? 's' : ''}`;
  };

  // Table columns
  const columns = [
    { 
      key: 'employee_name', 
      label: 'Employee', 
      sortable: true,
      render: (row) => <span className="font-medium">{row.employee_name || 'N/A'}</span>
    },
    { 
      key: 'days_count', 
      label: 'Duration',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Badge variant={row.is_half_day ? 'outline' : 'secondary'} className="font-normal">
            {formatDays(row)}
          </Badge>
        </div>
      )
    },
    { 
      key: 'start_date', 
      label: 'From', 
      sortable: true,
      render: (row) => formatDate(row.start_date)
    },
    { 
      key: 'end_date', 
      label: 'To',
      render: (row) => row.is_half_day ? '-' : formatDate(row.end_date)
    },
    { 
      key: 'reason', 
      label: 'Reason',
      render: (row) => <span className="max-w-xs truncate block">{row.reason}</span>
    },
    { 
      key: 'status', 
      label: 'Status',
      render: (row) => {
        const config = LEAVE_STATUS[row.status] || LEAVE_STATUS.pending;
        const Icon = config.icon;
        return (
          <Badge variant={config.variant} className="flex items-center gap-1 w-fit">
            <Icon className="w-3 h-3" />
            {config.label}
          </Badge>
        );
      }
    },
  ];

  // Columns with manager actions
  const managerColumns = [
    ...columns,
    {
      key: 'actions',
      label: '',
      render: (row) => row.status === 'pending' && (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" className="text-green-600" onClick={(e) => { e.stopPropagation(); handleApproval(row, 'approve', 'manager'); }}>
            <Check className="w-4 h-4 mr-1" /> Approve
          </Button>
          <Button size="sm" variant="outline" className="text-red-600" onClick={(e) => { e.stopPropagation(); handleApproval(row, 'reject', 'manager'); }}>
            <X className="w-4 h-4 mr-1" /> Reject
          </Button>
        </div>
      )
    }
  ];

  // Columns with final approval actions
  const finalColumns = [
    ...columns,
    {
      key: 'actions',
      label: '',
      render: (row) => row.status === 'manager_approved' && (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" className="text-green-600" onClick={(e) => { e.stopPropagation(); handleApproval(row, 'approve', 'final'); }}>
            <Check className="w-4 h-4 mr-1" /> Approve
          </Button>
          <Button size="sm" variant="outline" className="text-red-600" onClick={(e) => { e.stopPropagation(); handleApproval(row, 'reject', 'final'); }}>
            <X className="w-4 h-4 mr-1" /> Reject
          </Button>
        </div>
      )
    }
  ];

  if (loading) {
    return <PageSkeleton />;
  }

  const isEligible = balance?.is_eligible !== false;
  const elBalance = balance?.EL || 0;
  const hasBalance = elBalance > 0;
  
  // Build reason string for UI
  const ineligibilityReason = !isEligible 
    ? (balance?.reason || 'You are not eligible for earned leave.')
    : (!hasBalance 
      ? 'Your EL balance is 0. Quarterly credit may not have been processed yet — please contact HR.'
      : null);

  return (
    <div data-testid="leaves-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Leave Management</h1>
          <p className="text-muted-foreground mt-1">Earned Leave (EL) • Employee → Manager → HR approval flow</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Export Button */}
          <ExportButton 
            exportFn={exportAPI.leaves} 
            filename="leaves.csv" 
            label="Export"
          />
          
          {/* Calendar Link */}
          <Link to="/leaves/calendar">
            <Button variant="outline" className="gap-2">
              <CalendarDays className="w-4 h-4" />
              Team Calendar
            </Button>
          </Link>
          
          {/* Leave Balance */}
          {!balanceLoading && balance && (
            <div className="flex items-center gap-3 px-4 py-2 bg-primary/5 rounded-lg border">
              <div className="text-center">
                <p className="text-2xl font-bold text-primary">{elBalance}</p>
                <p className="text-xs text-muted-foreground">EL Balance</p>
              </div>
              {balance.carry_forward > 0 && (
                <div className="text-center border-l pl-3">
                  <p className="text-lg font-semibold text-muted-foreground">{balance.carry_forward}</p>
                  <p className="text-xs text-muted-foreground">Carry Forward</p>
                </div>
              )}
            </div>
          )}
          {!balanceLoading && !balance && balanceError && (
            <div className="flex items-center gap-2 px-4 py-2 bg-destructive/10 rounded-lg border border-destructive/30 cursor-pointer" onClick={refreshBalance} data-testid="balance-error">
              <div className="text-center">
                <p className="text-sm font-medium text-destructive">Balance unavailable</p>
                <p className="text-xs text-muted-foreground">
                  {balanceError?.response?.data?.detail || 'Could not load balance'} — tap to retry
                </p>
              </div>
            </div>
          )}
          {balanceLoading && (
            <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg border">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm text-muted-foreground">Loading balance...</span>
            </div>
          )}
          
          {/* Apply Leave Button */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="apply-leave-btn" disabled={!isEligible}>
                <Plus className="w-4 h-4 mr-2" /> Apply Leave
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Apply for Earned Leave</DialogTitle>
                <DialogDescription>
                  Available Balance: <span className="font-semibold text-primary">{elBalance} days</span>
                </DialogDescription>
              </DialogHeader>
              
              {!isEligible && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Not Eligible</AlertTitle>
                  <AlertDescription>
                    {balance?.reason || 'You are not eligible for earned leave.'}
                  </AlertDescription>
                </Alert>
              )}
              
              {isEligible && !hasBalance && (
                <Alert variant="default" className="border-amber-300 bg-amber-50 text-amber-900">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertTitle>No Leave Balance</AlertTitle>
                  <AlertDescription>
                    Your EL balance is 0. Quarterly EL credit may not have been processed yet — please contact HR to get your balance credited.
                  </AlertDescription>
                </Alert>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Half Day Toggle */}
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="half-day" className="cursor-pointer">Half Day Leave</Label>
                    <Badge variant="outline" className="text-xs">0.5 day</Badge>
                  </div>
                  <Switch
                    id="half-day"
                    checked={formData.is_half_day}
                    onCheckedChange={(checked) => {
                      setFormData({ 
                        ...formData, 
                        is_half_day: checked,
                        half_day_session: checked ? 'first_half' : null,
                        end_date: checked ? formData.start_date : formData.end_date
                      });
                    }}
                  />
                </div>
                
                {/* Half Day Session Selection */}
                {formData.is_half_day && (
                  <div className="space-y-2">
                    <Label>Session</Label>
                    <Select 
                      value={formData.half_day_session || ''} 
                      onValueChange={(v) => setFormData({ ...formData, half_day_session: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select session" />
                      </SelectTrigger>
                      <SelectContent>
                        {HALF_DAY_SESSIONS.map(session => (
                          <SelectItem key={session.value} value={session.value}>{session.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Date Selection */}
                <div className={formData.is_half_day ? '' : 'grid grid-cols-2 gap-4'}>
                  <div className="space-y-2">
                    <Label>{formData.is_half_day ? 'Date' : 'Start Date'}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(formData.start_date, 'dd/MM/yyyy')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar 
                          mode="single" 
                          selected={formData.start_date} 
                          onSelect={(d) => d && setFormData({ 
                            ...formData, 
                            start_date: d,
                            end_date: formData.is_half_day ? d : formData.end_date
                          })} 
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  {!formData.is_half_day && (
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {format(formData.end_date, 'dd/MM/yyyy')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar 
                            mode="single" 
                            selected={formData.end_date} 
                            onSelect={(d) => d && setFormData({ ...formData, end_date: d })} 
                            disabled={(date) => date < formData.start_date}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label>Reason</Label>
                  <Textarea 
                    value={formData.reason} 
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })} 
                    placeholder="Reason for leave..." 
                    required 
                  />
                </div>
                
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={creating || !isEligible || !hasBalance}>
                    {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* EL Policy Info */}
      <Alert className="bg-blue-50/50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertTitle className="text-blue-800">Earned Leave Policy</AlertTitle>
        <AlertDescription className="text-blue-700">
          12 EL per year • Credited quarterly (3 per quarter) • Only confirmed employees eligible • 
          Max 12 days carry forward • Excess can be encashed
        </AlertDescription>
      </Alert>

      {/* Stats Cards */}
      {(isAdmin || isSuperAdmin || isHR) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card 
            className="cursor-pointer hover:shadow-md hover:border-primary/50 hover:scale-[1.02] transition-all"
            onClick={() => setSearch('pending')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">Pending Manager</span>
                  </div>
                  <p className="text-2xl font-bold">{stats.pending}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:shadow-md hover:border-primary/50 hover:scale-[1.02] transition-all"
            onClick={() => setSearch('manager_approved')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">Pending Final</span>
                  </div>
                  <p className="text-2xl font-bold">{stats.managerApproved}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:shadow-md hover:border-primary/50 hover:scale-[1.02] transition-all"
            onClick={() => setSearch('approved')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 text-green-600 mb-1">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm">Approved</span>
                  </div>
                  <p className="text-2xl font-bold">{stats.approved}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:shadow-md hover:border-primary/50 hover:scale-[1.02] transition-all"
            onClick={() => setSearch('rejected')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 text-red-600 mb-1">
                    <XCircle className="w-4 h-4" />
                    <span className="text-sm">Rejected</span>
                  </div>
                  <p className="text-2xl font-bold">{stats.rejected}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <SearchInput
        value={search}
        onChange={setSearch}
        placeholder="Search by employee name or reason..."
        className="max-w-md"
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            All Requests
          </TabsTrigger>
          {!isEmployee && (
            <TabsTrigger value="team" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              My Team
              {pendingTeamLeaves.length > 0 && (
                <Badge variant="secondary" className="ml-1">{pendingTeamLeaves.length}</Badge>
              )}
            </TabsTrigger>
          )}
          {canApproveLeave && (
            <TabsTrigger value="pending-final" className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Final Approval
              {pendingFinalLeaves.length > 0 && (
                <Badge variant="secondary" className="ml-1">{pendingFinalLeaves.length}</Badge>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="all">
          <Card>
            <CardContent className="p-0">
              <DataTable
                data={filteredLeaves}
                columns={columns}
                sort={sort}
                onSort={setSort}
                emptyMessage="No leave requests found"
                emptyIcon={<FileText className="h-12 w-12 text-muted-foreground" />}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {!isEmployee && (
          <TabsContent value="team">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Team Leave Requests (Manager Approval)</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <DataTable
                  data={pendingTeamLeaves}
                  columns={managerColumns}
                  emptyMessage="No pending team requests"
                  emptyIcon={<Users className="h-12 w-12 text-muted-foreground" />}
                />
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {canApproveLeave && (
          <TabsContent value="pending-final">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Pending Final Approval (HR/Admin)</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <DataTable
                  data={pendingFinalLeaves}
                  columns={finalColumns}
                  emptyMessage="No leaves pending final approval"
                  emptyIcon={<CheckCircle className="h-12 w-12 text-muted-foreground" />}
                />
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => !open && setRejectDialog({ open: false, leave: null, level: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Leave Request</DialogTitle>
            <DialogDescription>Please provide a reason for rejection</DialogDescription>
          </DialogHeader>
          <Textarea 
            value={rejectReason} 
            onChange={(e) => setRejectReason(e.target.value)} 
            placeholder="Reason for rejection..." 
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, leave: null, level: '' })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleReject} disabled={!rejectReason.trim()}>
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
